<template>
    <div v-if="hasData" class="defi-activity-section">
        <div class="section-divider">
            <span class="section-title">DeFi & Trading Volume</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top DEX Programs by Volume</div>
                    <div class="panel-subtitle">One-sided volume (no double-count)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="defiVolumeChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">DEX Program Breakdown</div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive" style="max-height: 300px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>DEX Program</th>
                                    <th class="text-end">Volume</th>
                                    <th class="text-end">Trades</th>
                                    <th class="text-end">Markets</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="p in dexPrograms" :key="p.program">
                                    <td>
                                        <a :href="`/analytics/programs/${p.program}`" class="text-gradient">
                                            {{ programLabel(p) }}
                                        </a>
                                    </td>
                                    <td class="text-end font-mono">${{ formatCompact(p.volume) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(p.trades) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(p.markets) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Aggregator Section -->
        <div v-if="aggregators.length > 0" class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Aggregators by Routed Volume</div>
                    <div class="panel-subtitle">Volume routed through Jupiter, DFlow, OKX, etc.</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="aggregatorVolumeChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Aggregator Breakdown</div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive" style="max-height: 300px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Aggregator</th>
                                    <th class="text-end">Volume Routed</th>
                                    <th class="text-end">Trades</th>
                                    <th class="text-end">Traders</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="p in aggregators" :key="p.program">
                                    <td>
                                        <a :href="`/analytics/programs/${p.program}`" class="text-gradient">
                                            {{ programLabel(p) }}
                                        </a>
                                    </td>
                                    <td class="text-end font-mono">${{ formatCompact(p.volume) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(p.trades) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(p.traders) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatPubkey, programLabel } from '../../composables/useFormatters';
import { colors, getBarChartOptions } from '../../composables/useChartConfig';

export default {
    name: 'ProgramDefiActivity',

    props: {
        dexPrograms: {
            type: Array,
            default: () => []
        },
        aggregators: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            charts: {},
            colors: colors
        };
    },

    computed: {
        hasData() {
            return this.dexPrograms.length > 0;
        }
    },

    watch: {
        dexPrograms: {
            handler() {
                this.$nextTick(() => {
                    if (this.hasData) {
                        if (Object.keys(this.charts).length > 0) {
                            this.updateCharts();
                        } else {
                            this.initCharts();
                        }
                    }
                });
            },
            deep: true
        },
        aggregators: {
            handler() {
                if (this.charts.aggregatorVolume) {
                    this.updateAggregatorChart();
                }
            },
            deep: true
        }
    },

    mounted() {
        if (this.hasData) {
            this.$nextTick(() => this.initCharts());
        }
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    methods: {
        formatCompact,
        formatPubkey,
        programLabel,

        initCharts() {
            if (!this.$refs.defiVolumeChart) return;

            // DeFi Volume Chart
            this.charts.defiVolume = markRaw(new Chart(this.$refs.defiVolumeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false,
                    formatCallback: v => '$' + formatCompact(v)
                })
            }));

            // Aggregator Volume Chart
            if (this.$refs.aggregatorVolumeChart) {
                this.charts.aggregatorVolume = markRaw(new Chart(this.$refs.aggregatorVolumeChart, {
                    type: 'bar',
                    data: { labels: [], datasets: [] },
                    options: getBarChartOptions({
                        horizontal: true,
                        showLegend: false,
                        formatCallback: v => '$' + formatCompact(v)
                    })
                }));
            }

            this.updateCharts();
        },

        updateCharts() {
            this.updateDefiChart();
            this.updateAggregatorChart();
        },

        updateDefiChart() {
            if (!this.charts.defiVolume || !this.dexPrograms.length) return;

            this.charts.defiVolume.data = {
                labels: this.dexPrograms.map(p => programLabel(p)),
                datasets: [{
                    data: this.dexPrograms.map(p => p.volume),
                    backgroundColor: this.colors.series
                }]
            };
            this.charts.defiVolume.update();
        },

        updateAggregatorChart() {
            if (!this.charts.aggregatorVolume || !this.aggregators.length) return;

            this.charts.aggregatorVolume.data = {
                labels: this.aggregators.map(p => programLabel(p)),
                datasets: [{
                    data: this.aggregators.map(p => p.volume),
                    backgroundColor: this.colors.series
                }]
            };
            this.charts.aggregatorVolume.update();
        }
    }
};
</script>

<style scoped>
.chart-tall {
    min-height: 280px;
}
.font-mono {
    font-family: 'JetBrains Mono', monospace;
}
.text-gradient {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}
</style>
